// useAuthedBlobUrls — the multi-key sibling of `useAuthedBlobUrl`
// (`unify-image-lightbox`). Surfaces that render a list of authed images and a
// lightbox over that same list (chat history, response assets, report blocks)
// lift the fetching to the list level with this hook, so the thumbnails and the
// lightbox read ONE map and a thumbnail that already loaded is never fetched a
// second time just because the reader opened it.
//
// Lifecycle, per key: fetched exactly once while it stays in `keys`; its object
// URL is revoked as soon as the key leaves `keys`; every remaining URL is
// revoked on unmount. A result that lands after its key left (or after
// unmount) is ignored and no object URL is ever created for it, so nothing can
// leak and no state is written after the fact.
//
// Same ref rules as `useAuthedBlobUrl`: `fetchBlob` is NOT an effect
// dependency — callers close over per-call context (session id, message id)
// and hand a new function identity every render — it is captured in a ref
// that is always current, so the latest closure is the one used for keys added
// later. Callers build a new `keys` array on every render
// (`assets.map((a) => a.relation_id)`), so the sync effect depends on a
// content-stable signature of the keys (their NUL-joined text), not on the
// array's identity: it runs when the key set changes and not otherwise. It
// reads the keys through a ref for the same reason as `fetchBlob`. Inside, it
// diffs the keys against the per-key registry and only touches state when a
// key was actually added or removed.
//
// `lightboxImageOf` is the one mapping from a key's `BlobUrlState` to the
// `ImageLightbox`'s image states, shared by every surface built on this hook.

import { useEffect, useRef, useState } from "react";

import type { LightboxImage } from "@/components/ui/image-lightbox";
import type { BlobUrlState } from "@/lib/use-authed-blob-url";

// One in-flight or settled request. `cancelled` is flipped when the key leaves
// the list (or the hook unmounts) so the late `then` knows to drop its result.
interface Entry {
  cancelled: boolean;
  url: string | null;
}

const LOADING: BlobUrlState = { url: null, loading: true, error: null };

/**
 * What the lightbox shows for an image, from the state its blob is in. A key
 * the map does not hold yet (the render before its fetch starts) is loading.
 */
export function lightboxImageOf(state: BlobUrlState | undefined): LightboxImage {
  if (state === undefined) return { status: "loading" };
  if (state.error !== null) return { status: "unavailable" };
  if (state.url !== null) return { status: "ready", url: state.url };
  return { status: "loading" };
}

/**
 * Fetch an authed blob per key and expose one object URL per key.
 *
 * Returns a map from key to its `BlobUrlState`. A key that is in `keys` but
 * not yet in the map (the render before the effect that starts its fetch has
 * run) MUST be treated by callers as loading; a key that is not in `keys` is
 * never in the map. The map keeps its identity until some entry changes.
 */
export function useAuthedBlobUrls(
  keys: readonly string[],
  fetchBlob: (key: string) => Promise<Blob>,
): ReadonlyMap<string, BlobUrlState> {
  const [states, setStates] = useState<ReadonlyMap<string, BlobUrlState>>(() => new Map());
  const entriesRef = useRef(new Map<string, Entry>());
  const fetchBlobRef = useRef(fetchBlob);
  fetchBlobRef.current = fetchBlob;
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const keysSignature = keys.join("\u0000");

  // Unmount: revoke everything and drop in-flight results. Declared before the
  // sync effect so that, if React re-runs effects (strict mode), this one is
  // set up again first and the sync effect then restarts the cleared entries.
  useEffect(() => {
    const entries = entriesRef.current;
    return () => {
      for (const entry of entries.values()) {
        entry.cancelled = true;
        if (entry.url !== null) URL.revokeObjectURL(entry.url);
      }
      entries.clear();
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `keysSignature` is the deliberate trigger — the effect reads the keys through `keysRef`, and the signature makes it run when their content changes instead of on every fresh array
  useEffect(() => {
    const entries = entriesRef.current;
    const wanted = new Set(keysRef.current);

    const removed: string[] = [];
    for (const [key, entry] of entries) {
      if (wanted.has(key)) continue;
      entry.cancelled = true;
      if (entry.url !== null) URL.revokeObjectURL(entry.url);
      entries.delete(key);
      removed.push(key);
    }

    const added: string[] = [];
    for (const key of wanted) {
      if (entries.has(key)) continue;
      const entry: Entry = { cancelled: false, url: null };
      entries.set(key, entry);
      added.push(key);

      fetchBlobRef
        .current(key)
        .then((blob) => {
          if (entry.cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          entry.url = objectUrl;
          setStates((prev) =>
            new Map(prev).set(key, { url: objectUrl, loading: false, error: null }),
          );
        })
        .catch((err: unknown) => {
          if (entry.cancelled) return;
          const message = err instanceof Error ? err.message : "fetch failed";
          setStates((prev) =>
            new Map(prev).set(key, { url: null, loading: false, error: message }),
          );
        });
    }

    if (removed.length === 0 && added.length === 0) return;
    setStates((prev) => {
      const next = new Map(prev);
      for (const key of removed) next.delete(key);
      for (const key of added) next.set(key, LOADING);
      return next;
    });
  }, [keysSignature]);

  return states;
}
